import {
  ATTACHMENT_MAX_FILE_BYTES,
  ATTACHMENT_PERMISSIONS,
  type Attachment,
  type AttachmentEntityKind,
} from '@palaccount/contracts';
import { Download, FileText, Loader2, Paperclip, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause } from '@/lib/server-messages';

import { attachmentUrl, formatBytes, useAttachments, useRemoveAttachment, useUploadAttachment } from './api';

/**
 * INV-H5 / IT-S8 — THE ATTACHMENTS CARD, mounted at the foot of a document
 * editor or a master-record form.
 *
 * One component for every kind of record: the kind and the record id are the
 * only things a screen tells it. Permission follows the record
 * (`ATTACHMENT_PERMISSIONS[kind]`) — the upload button is `<Gated>` on the
 * record's EDIT permission and the server fences the same way, so greying
 * here is UX and never the control (PART 16.7 row 2).
 *
 * A NEW, unsaved record has no id to pin a file to, so the card draws one
 * muted line instead of an upload button that could only fail: "Save first".
 * `readOnly` is the screen's own say — a posted or cancelled document whose
 * files are part of its record — and hides upload and delete regardless of
 * permission.
 *
 * Images draw a thumbnail from the download URL itself (the session cookie
 * rides along); everything else gets a file glyph. The name is a plain link
 * with `download`, the documents route's pattern: the browser's own download
 * UI takes over and a 10 MB scan never sits in the tab's memory.
 */
export function AttachmentsPanel({
  kind,
  entityId,
  readOnly = false,
}: {
  kind: AttachmentEntityKind;
  /** Undefined or empty on a record that has not been saved yet. */
  entityId: string | undefined;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const saved = entityId !== undefined && entityId !== '';
  const { data, isPending, isError } = useAttachments(kind, saved ? entityId : undefined);
  const upload = useUploadAttachment(kind, entityId ?? '');
  const remove = useRemoveAttachment(kind, entityId ?? '');
  const canEdit = useCan(ATTACHMENT_PERMISSIONS[kind].edit);
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingDelete, setPendingDelete] = useState<Attachment | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const mayWrite = saved && !readOnly;

  /**
   * One request per file, in order, so a 413 on the third names the third and
   * the first two are already on the list. Every failure is kept, not just the
   * last: a person dropping six scans wants to know which two to retry.
   */
  const onFilesChosen = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    const failures: string[] = [];
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync(file);
      } catch (caught) {
        failures.push(uploadFailure(caught, file.name));
      }
    }
    setErrors(failures);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const uploadFailure = (caught: unknown, name: string): string => {
    if (caught instanceof ApiError && caught.status === 413) {
      return t('attachments.errTooLarge', { name, max: formatBytes(ATTACHMENT_MAX_FILE_BYTES) });
    }
    if (caught instanceof ApiError && caught.status === 507) {
      return t('attachments.errQuota', { quota: formatBytes(data?.quotaBytes ?? 0) });
    }
    return `${name}: ${localizeServerCause(t, caught, 'attachments.errUploadFailed')}`;
  };

  const onConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.id);
    } catch (caught) {
      setErrors([localizeServerCause(t, caught, 'attachments.errUploadFailed')]);
    } finally {
      setPendingDelete(null);
    }
  };

  const rows = data?.attachments ?? [];

  return (
    <Card data-testid="attachments-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2">
          <Paperclip className="size-4" aria-hidden />
          {t('attachments.title')}
          {saved && rows.length > 0 ? (
            <span className="text-sm font-normal text-muted-foreground">({rows.length})</span>
          ) : null}
        </CardTitle>
        {mayWrite ? (
          <Gated permission={ATTACHMENT_PERMISSIONS[kind].edit} reasonKey="perm.cannot.attachFiles">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
            >
              {busy ? <Loader2 className="animate-spin" aria-hidden /> : <Paperclip aria-hidden />}
              {t(busy ? 'attachments.uploading' : 'attachments.upload')}
            </Button>
          </Gated>
        ) : null}
        {mayWrite && canEdit ? (
          <input
            ref={inputRef}
            type="file"
            multiple
            className="sr-only"
            aria-label={t('attachments.fileInput')}
            data-testid="attachments-input"
            onChange={(event) => void onFilesChosen(event.target.files)}
          />
        ) : null}
      </CardHeader>
      <CardContent className="grid gap-3">
        {!saved ? (
          <p className="text-sm text-muted-foreground">{t('attachments.saveFirst')}</p>
        ) : isPending ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : isError ? (
          <p className="text-sm text-destructive">{t('common.loadFailed')}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('attachments.empty')}</p>
        ) : (
          <ul className="grid gap-2">
            {rows.map((row) => (
              <li
                key={row.id}
                className="flex items-center gap-3 rounded-md border border-border px-3 py-2 text-sm"
              >
                {row.contentType.startsWith('image/') ? (
                  <img
                    src={attachmentUrl(row.id)}
                    alt=""
                    className="size-10 shrink-0 rounded object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span
                    className="flex size-10 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground"
                    aria-hidden
                  >
                    <FileText className="size-5" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <a
                    href={attachmentUrl(row.id)}
                    download={row.fileName}
                    className="block truncate font-medium hover:underline"
                  >
                    <bdi>{row.fileName}</bdi>
                  </a>
                  <p className="truncate text-xs text-muted-foreground">
                    <Ltr value={formatBytes(row.byteSize)} />
                    {' · '}
                    {row.uploadedByName ?? t('md.authorRemoved')}
                    {' · '}
                    <Ltr value={row.createdAt.slice(0, 16).replace('T', ' ')} className="font-mono" />
                  </p>
                </div>
                <Button
                  asChild
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('attachments.download')}
                  title={t('attachments.download')}
                >
                  <a href={attachmentUrl(row.id)} download={row.fileName}>
                    <Download aria-hidden />
                  </a>
                </Button>
                {mayWrite && canEdit ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t('attachments.remove')}
                    title={t('attachments.remove')}
                    onClick={() => setPendingDelete(row)}
                  >
                    <Trash2 aria-hidden />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {errors.length > 0 ? (
          <ul className="grid gap-1 text-sm text-destructive" role="alert">
            {errors.map((message, index) => (
              <li key={`${index}-${message}`}>{message}</li>
            ))}
          </ul>
        ) : null}
        {saved && data ? (
          <p className="text-xs text-muted-foreground">
            {t('attachments.usage', {
              used: formatBytes(data.usedBytes),
              quota: formatBytes(data.quotaBytes),
            })}
          </p>
        ) : null}
      </CardContent>
      <DeleteConfirmBox
        open={pendingDelete !== null}
        detail={
          pendingDelete ? t('attachments.confirmDelete', { name: pendingDelete.fileName }) : undefined
        }
        onConfirm={() => void onConfirmDelete()}
        onCancel={() => setPendingDelete(null)}
        busy={remove.isPending}
      />
    </Card>
  );
}
